import { DownloadItemIcon } from '@/assets/static/icons/download-item';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useState } from 'react';
import styled from 'styled-components';
import Button from '../basicComponents/button';
import DatePicker from '../basicComponents/datePicker';
import Header from '../header';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { GET_SETTINGS_QUERY } from '../settings-page/queries';
import Failure from './modals/failure';

export const ReportingData = () => {
  const { isMobile, isTablet } = useResponsive();

  const [datesValue, setDatesValue] = useState<{ startDate: string | null; endDate: string | null }>({ startDate: null, endDate: null });
  const [failureModalWindow, setFailureModalWindow] = useState({ isOpen: false, error: '' });
  const [loading, setLoading] = useState(false);
  const [moduleConfig, setModuleConfig] = useState(null);

  const { loading: settingsLoading } = useQuery(GET_SETTINGS_QUERY, {
    variables: {
      type: 'appSetting'
    },
    onCompleted: (data) => {
      if (data?.Settings[0]?.value) {
        setModuleConfig(JSON.parse(data?.Settings[0]?.value) ?? {});
      } else {
        setModuleConfig({});
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/commitment/reporting/${moduleConfig?.['Reporting']}`,
        {
          startDate: datesValue.startDate,
          endDate: datesValue.endDate,
          exportType: moduleConfig?.['Reporting']
        },
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      fileDownload(response.data, moduleConfig?.['Reporting'] === 'tamarac' ? 'reporting_export.csv' : 'reporting_export.zip');
    } catch (error: any) {
      setFailureModalWindow({ isOpen: true, error: await error.response.data.text() });
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      <Failure
        errorMessage={failureModalWindow.error}
        onClose={() => setFailureModalWindow({ isOpen: false, error: '' })}
        isOpen={failureModalWindow.isOpen}
      />
      <MainWrap>
        <Header />
        <PaddingWrap>
          <ExportWrapper isMobile={isMobile}>
            <ReportingDataImgWrapper>
              <ReportingDataImg src={`/static/mock-images/${moduleConfig?.['Reporting']}.webp`} draggable="false" alt="Reporting Data" />
            </ReportingDataImgWrapper>
            <DatesWrapper isTablet={isTablet} isMobile={isMobile}>
              <DatePicker
                label="Start Date"
                value={datesValue.startDate}
                onChange={(value) => setDatesValue({ ...datesValue, startDate: value })}
                size="md"
              />
              <DatePicker
                label="End Date"
                value={datesValue.endDate}
                onChange={(value) => setDatesValue({ ...datesValue, endDate: value })}
                size="md"
              />
              <div>
                <Button
                  size="md"
                  styleType={loading ? 'disabled' : 'default'}
                  onClick={handleDownload}
                  isLoading={loading}
                  icon={<DownloadItemIcon fill={themeVar('context.light')} />}
                >
                  Download
                </Button>
              </div>
            </DatesWrapper>
          </ExportWrapper>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaddingWrap = styled.div`
  padding: 50px 16px 0 16px;
  @media (min-width: 600px) {
    padding: 50px 50px 0 50px;
  }
`;

const ReportingDataImg = styled.img`
  width: 100%;
  height: 100%;
`;

const ReportingDataImgWrapper = styled.div`
  width: 215px;
`;

const ExportWrapper = styled.div<{ isMobile: boolean }>`
  padding: 40px;
  background: ${themeVar('layer.1')};
  border-radius: 10px;
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
`;

const DatesWrapper = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  width: 60%;
  display: flex;
  flex-direction: ${({ isTablet, isMobile }) => (isTablet || isMobile ? 'column' : 'row')};
  align-items: flex-end;
  gap: 20px;
`;
